const ANIMATE_CLASS = "page-animate";
const SHELL_READY_CLASS = "page-shell-ready";
const VISIBLE_CLASS = "is-visible";

type AnimateConfig = {
  rootMargin?: string;
  threshold?: number;
};

const defaultConfig: AnimateConfig = {
  rootMargin: "0px 0px -10% 0px",
  threshold: 0.1,
};

let observer: IntersectionObserver | null = null;
let animationsEnabled = false;
let initialized = false;
let reducedMotion = false;

function markShellReady() {
  const potentialShells: Array<HTMLElement | null> = [
    document.body,
    document.getElementById("root") as HTMLElement | null,
    document.querySelector<HTMLElement>(".page-shell"),
  ];

  for (const shell of potentialShells) {
    if (!shell) continue;
    if (!shell.classList.contains(SHELL_READY_CLASS)) {
      shell.classList.add(SHELL_READY_CLASS);
    }
  }
}

function ensureShellReady() {
  if (typeof window === "undefined") return;
  const attempt = () => {
    markShellReady();
    const shell =
      document.body ||
      (document.getElementById("root") as HTMLElement | null) ||
      document.querySelector<HTMLElement>(".page-shell");
    if (!shell || !shell.classList.contains(SHELL_READY_CLASS)) {
      requestAnimationFrame(attempt);
    }
  };
  requestAnimationFrame(attempt);
}

export function refreshPageAnimations() {
  if (typeof window === "undefined") return;

  const nodes = Array.from(
    document.querySelectorAll<HTMLElement>("[data-animate]")
  );

  const obs = observer;
  if (!animationsEnabled || !obs) {
    nodes.forEach((node) => node.classList.add(VISIBLE_CLASS));
    return;
  }

  nodes.forEach((node) => {
    if (!node.classList.contains(VISIBLE_CLASS)) {
      obs.observe(node);
    }
  });
}

export function initPageAnimations(config: AnimateConfig = defaultConfig) {
  if (typeof window === "undefined" || initialized) return;
  initialized = true;

  reducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  animationsEnabled = !reducedMotion && "IntersectionObserver" in window;

  if (!animationsEnabled) {
    requestAnimationFrame(() => {
      document.documentElement.classList.add(ANIMATE_CLASS);
      ensureShellReady();
      refreshPageAnimations();
    });
    return;
  }

  observer = new IntersectionObserver(
    (entries, obs) => {
      for (const entry of entries) {
        if (entry.isIntersecting) {
          entry.target.classList.add(VISIBLE_CLASS);
          obs.unobserve(entry.target);
        }
      }
    },
    {
      rootMargin: config.rootMargin ?? defaultConfig.rootMargin,
      threshold: config.threshold ?? defaultConfig.threshold,
    }
  );

  requestAnimationFrame(() => {
    document.documentElement.classList.add(ANIMATE_CLASS);
    ensureShellReady();
    refreshPageAnimations();
  });
}

export function setPageShellReady() {
  if (typeof window === "undefined") return;
  markShellReady();
}
